import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  getDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { getPerformance } from 'firebase/performance';
import app from './firebase';
import { University, Document, Email, Deadline } from '@/types';

export const db = getFirestore(app);

// Firebase Performance Monitoring — auto-collects page load, FCP, TTI, network
export const perf = getPerformance(app);

// Subscription types
export interface UserSubscription {
  plan: 'free' | 'premium';
  interval?: 'quarterly' | 'halfyearly' | 'yearly' | 'lifetime' | 'testimonial_reward' | string;
  purchasedAt?: string;
  expiresAt?: string;
  paymentId?: string;
  orderId?: string;
}

// Subscription CRUD
export const getUserSubscriptionRef = (userId: string) =>
  doc(db, 'users', userId, 'account', 'subscription');

export const setUserSubscription = async (userId: string, subscription: UserSubscription) => {
  const ref = getUserSubscriptionRef(userId);
  await setDoc(ref, {
    ...subscription,
    updatedAt: Timestamp.now()
  }, { merge: true });
};

export const getUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
  const ref = getUserSubscriptionRef(userId);
  const docSnap = await getDoc(ref);
  if (docSnap.exists()) {
    return docSnap.data() as UserSubscription;
  }
  return null;
};

export const subscribeToUserSubscription = (
  userId: string,
  callback: (subscription: UserSubscription | null) => void,
  onError?: (error: unknown) => void
) => {
  const ref = getUserSubscriptionRef(userId);
  return onSnapshot(
    ref,
    (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data() as UserSubscription);
      } else {
        callback(null);
      }
    },
    (error) => onError?.(error)
  );
};

const cleanUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: Record<string, any> = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined) out[k] = v;
  });
  return out as Partial<T>;
};

// Collection references
export const getUniversitiesRef = (userId: string) =>
  collection(db, 'users', userId, 'universities');

export const getDocumentsRef = (userId: string) =>
  collection(db, 'users', userId, 'documents');

export const getEmailsRef = (userId: string) =>
  collection(db, 'users', userId, 'emails');

export const getDeadlinesRef = (userId: string) =>
  collection(db, 'users', userId, 'deadlines');

export const getSopLorRef = (userId: string) =>
  collection(db, 'users', userId, 'sopLorEntries');

// SOP/LOR CRUD
export interface SOPLOREntry {
  id: string;
  universityId: string;
  universityName: string;
  course?: string;
  type: 'sop' | 'lor';
  title: string;
  content: string;
  status: 'not_started' | 'drafting' | 'review' | 'revision' | 'final';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export const addSopLorEntry = async (userId: string, entry: Omit<SOPLOREntry, 'id' | 'createdAt' | 'updatedAt'>) => {
  const ref = getSopLorRef(userId);
  const now = Timestamp.now().toDate().toISOString();
  const docRef = await addDoc(ref, cleanUndefined({ ...entry, createdAt: now, updatedAt: now }));
  return docRef.id;
};

export const updateSopLorEntry = async (userId: string, entryId: string, updates: Partial<SOPLOREntry>) => {
  const docRef = doc(db, 'users', userId, 'sopLorEntries', entryId);
  await updateDoc(docRef, cleanUndefined({ ...updates, updatedAt: Timestamp.now().toDate().toISOString() }));
};

export const deleteSopLorEntry = async (userId: string, entryId: string) => {
  const docRef = doc(db, 'users', userId, 'sopLorEntries', entryId);
  await deleteDoc(docRef);
};

export const subscribeToSopLorEntries = (
  userId: string,
  callback: (entries: SOPLOREntry[]) => void,
  onError?: (error: unknown) => void
) => {
  const ref = getSopLorRef(userId);
  return onSnapshot(
    ref,
    (snapshot) => {
      const entries = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as SOPLOREntry[];
      callback(entries);
    },
    (error) => onError?.(error)
  );
};

// Universities CRUD
export const addUniversityToFirestore = async (userId: string, university: Omit<University, 'id'>) => {
  const ref = getUniversitiesRef(userId);
  const docRef = await addDoc(ref, cleanUndefined({
    ...university,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  }));
  return docRef.id;
};

export const updateUniversityInFirestore = async (userId: string, universityId: string, updates: Partial<University>) => {
  const docRef = doc(db, 'users', userId, 'universities', universityId);
  await updateDoc(docRef, cleanUndefined({
    ...updates,
    updatedAt: Timestamp.now()
  }));
};

export const deleteUniversityFromFirestore = async (userId: string, universityId: string) => {
  const docRef = doc(db, 'users', userId, 'universities', universityId);
  await deleteDoc(docRef);
};

// Documents CRUD
export const addDocumentToFirestore = async (userId: string, document: Omit<Document, 'id'>) => {
  const ref = getDocumentsRef(userId);
  const docRef = await addDoc(ref, cleanUndefined({
    ...document,
    uploadedAt: Timestamp.now()
  }));
  return docRef.id;
};

export const updateDocumentInFirestore = async (userId: string, documentId: string, updates: Partial<Document>) => {
  const docRef = doc(db, 'users', userId, 'documents', documentId);
  await updateDoc(docRef, cleanUndefined(updates));
};

export const deleteDocumentFromFirestore = async (userId: string, documentId: string) => {
  const docRef = doc(db, 'users', userId, 'documents', documentId);
  await deleteDoc(docRef);
};

// Emails CRUD
export const addEmailToFirestore = async (userId: string, email: Omit<Email, 'id'>) => {
  const ref = getEmailsRef(userId);
  const docRef = await addDoc(ref, cleanUndefined({
    ...email,
  }));
  return docRef.id;
};

export const updateEmailInFirestore = async (userId: string, emailId: string, updates: Partial<Email>) => {
  const docRef = doc(db, 'users', userId, 'emails', emailId);
  await updateDoc(docRef, cleanUndefined(updates));
};

export const markEmailReadInFirestore = async (userId: string, emailId: string) => {
  const docRef = doc(db, 'users', userId, 'emails', emailId);
  await updateDoc(docRef, { isRead: true });
};

export const toggleEmailReadInFirestore = async (userId: string, emailId: string, isRead: boolean) => {
  const docRef = doc(db, 'users', userId, 'emails', emailId);
  await updateDoc(docRef, { isRead });
};

export const deleteEmailFromFirestore = async (userId: string, emailId: string) => {
  const docRef = doc(db, 'users', userId, 'emails', emailId);
  await deleteDoc(docRef);
};

export const deleteAllEmailsFromFirestore = async (userId: string) => {
  const ref = getEmailsRef(userId);
  const snapshot = await import('firebase/firestore').then(m => m.getDocs(ref));
  const deletePromises = snapshot.docs.map(docSnap => deleteDoc(docSnap.ref));
  await Promise.all(deletePromises);
};

export const deleteEmailsBeforeDate = async (userId: string, beforeDate: Date) => {
  const ref = getEmailsRef(userId);
  const snapshot = await import('firebase/firestore').then(m => m.getDocs(ref));
  const deletePromises = snapshot.docs
    .filter(docSnap => {
      const receivedAt = (docSnap.data() as any).receivedAt;
      const emailDate = receivedAt?.toDate?.() || new Date(receivedAt);
      return emailDate < beforeDate;
    })
    .map(docSnap => deleteDoc(docSnap.ref));
  await Promise.all(deletePromises);
};

// Delete oldest emails to keep only the specified count
export const deleteOldestEmailsExceedingLimit = async (userId: string, maxEmails: number = 100) => {
  const ref = getEmailsRef(userId);
  const snapshot = await import('firebase/firestore').then(m => m.getDocs(ref));

  if (snapshot.docs.length <= maxEmails) {
    return 0; // No emails to delete
  }

  // Sort by receivedAt date
  const sortedDocs = snapshot.docs.sort((a, b) => {
    const aDate = (a.data() as any).receivedAt;
    const bDate = (b.data() as any).receivedAt;
    const aTime = aDate?.toDate?.()?.getTime() || new Date(aDate).getTime();
    const bTime = bDate?.toDate?.()?.getTime() || new Date(bDate).getTime();
    return bTime - aTime; // Newest first
  });

  // Keep the newest 100, delete the rest
  const docsToDelete = sortedDocs.slice(maxEmails);
  const deletePromises = docsToDelete.map(docSnap => deleteDoc(docSnap.ref));
  await Promise.all(deletePromises);

  return docsToDelete.length;
};

// Delete emails older than 24 hours
export const deleteEmailsOlderThan24Hours = async (userId: string) => {
  const ref = getEmailsRef(userId);
  const snapshot = await import('firebase/firestore').then(m => m.getDocs(ref));
  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

  const deletePromises = snapshot.docs
    .filter(docSnap => {
      const receivedAt = (docSnap.data() as any).receivedAt;
      const emailDate = receivedAt?.toDate?.() || new Date(receivedAt);
      return emailDate < twentyFourHoursAgo;
    })
    .map(docSnap => deleteDoc(docSnap.ref));

  await Promise.all(deletePromises);
  return deletePromises.length;
};

// Deadlines CRUD
export const addDeadlineToFirestore = async (userId: string, deadline: Omit<Deadline, 'id'>) => {
  const ref = getDeadlinesRef(userId);
  const docRef = await addDoc(ref, cleanUndefined(deadline));
  return docRef.id;
};

export const toggleDeadlineInFirestore = async (userId: string, deadlineId: string, completed: boolean) => {
  const docRef = doc(db, 'users', userId, 'deadlines', deadlineId);
  await updateDoc(docRef, { completed });
};

export const deleteDeadlineFromFirestore = async (userId: string, deadlineId: string) => {
  const docRef = doc(db, 'users', userId, 'deadlines', deadlineId);
  await deleteDoc(docRef);
};

// Real-time listeners
export const subscribeToUniversities = (
  userId: string,
  callback: (universities: University[]) => void,
  onError?: (error: unknown) => void
) => {
  const ref = getUniversitiesRef(userId);
  return onSnapshot(
    ref,
    (snapshot) => {
      const universities = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: (docSnap.data() as any).createdAt?.toDate?.()?.toISOString() || (docSnap.data() as any).createdAt,
        updatedAt: (docSnap.data() as any).updatedAt?.toDate?.()?.toISOString() || (docSnap.data() as any).updatedAt,
      })) as University[];
      callback(universities);
    },
    (error) => onError?.(error)
  );
};

export const subscribeToDocuments = (
  userId: string,
  callback: (documents: Document[]) => void,
  onError?: (error: unknown) => void
) => {
  const ref = getDocumentsRef(userId);
  return onSnapshot(
    ref,
    (snapshot) => {
      const documents = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        uploadedAt: (docSnap.data() as any).uploadedAt?.toDate?.()?.toISOString() || (docSnap.data() as any).uploadedAt,
      })) as Document[];
      callback(documents);
    },
    (error) => onError?.(error)
  );
};

export const subscribeToEmails = (
  userId: string,
  callback: (emails: Email[]) => void,
  onError?: (error: unknown) => void
) => {
  const ref = getEmailsRef(userId);
  return onSnapshot(
    ref,
    (snapshot) => {
      const emails = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        receivedAt: (docSnap.data() as any).receivedAt?.toDate?.()?.toISOString() || (docSnap.data() as any).receivedAt,
      })) as Email[];
      callback(emails);
    },
    (error) => onError?.(error)
  );
};

export const subscribeToDeadlines = (
  userId: string,
  callback: (deadlines: Deadline[]) => void,
  onError?: (error: unknown) => void
) => {
  const ref = getDeadlinesRef(userId);
  return onSnapshot(
    ref,
    (snapshot) => {
      const deadlines = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as Deadline[];
      callback(deadlines);
    },
    (error) => onError?.(error)
  );
};
